import { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { PresentUrl, bankFormApi, bankFormFetchApi, bankFormPennyDropApi } from "../../../text/apidata.js";
import ImageCropper from "../ReactcropOverall/Imagecrop.js";
import { store } from "../../../Redux/Store.js";
import { responsiveToast } from "../../AppComponent/responsiveToast.js";
import { getLoaderAction2 } from "../../../Redux/Loader2.js";
import {
  selectEmployeeInformation,
  updateBankField,
  setBankImage,
  removeBankImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./BankDetails.module.scss";

const removeHonorifics = (nameStr) => {
  if (!nameStr) return "";
  return String(nameStr)
    .replace(/^(MR|MRS|MS|DR|SHRI|SMT|M\/S)\.?\s+/i, "")
    .trim();
};

const BankDetails = forwardRef(function BankDetails(_, ref) {
  const dispatch = useDispatch();

  const { bankDetails, bankImages, basicInformation } = useSelector(selectEmployeeInformation);

  const selfonboardData = useSelector(
    (state) => state.selfonboardingretriveslice?.Selfonboarding_Data
  );
  const employeeDetails = useSelector(
    (state) => state.Signature_Employee_RetrieveCall_actions?.Signature_Employee_Datas
  );

  const [errorFields, setErrorFields] = useState(new Set());
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImageEvent, setSelectedImageEvent] = useState(null);
  const [isVerifyingBank, setIsVerifyingBank] = useState(false);
  const [isAccountNumDisabled, setIsAccountNumDisabled] = useState(false);
  const [isHolderNameDisabled, setIsHolderNameDisabled] = useState(false);

  const extractBankFormId = (d) => {
    if (!d) return null;
    const raw =
      d?.bank_details_form?.id ||
      d?.bank_details_form ||
      d?.employeetimeline_id?.bank_details_form ||
      d?.employeetimeline_id?.bank_details ||
      d?.bank_details_id ||
      d?.bank_details ||
      d?.bankform_id;

    if (typeof raw === "object" && raw !== null) {
      return raw.id || raw._id || null;
    }
    return raw ? String(raw) : null;
  };

  // Prefill phone_number from Basic Information
  useEffect(() => {
    const rawPhone =
      basicInformation.phoneNum ||
      basicInformation.phone_number ||
      selfonboardData?.phone_number ||
      selfonboardData?.basicform_id?.phone_number ||
      employeeDetails?.phone_number ||
      "";

    if (rawPhone && !bankDetails.phone_number) {
      dispatch(
        updateBankField({
          name: "phone_number",
          value: String(rawPhone).replace(/\D/g, ""),
        })
      );
    }
  }, [dispatch, basicInformation, selfonboardData, employeeDetails, bankDetails.phone_number]);

  // GET Bank Details API call on mount
  const fetchedBankFormIdRef = useRef(null);

  useEffect(() => {
    const details = selfonboardData || employeeDetails;
    if (!details) {
      dispatch(getLoaderAction2(false));
      return;
    }

    const bankFormId = extractBankFormId(details);
    if (!bankFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }

    if (fetchedBankFormIdRef.current === bankFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }
    fetchedBankFormIdRef.current = bankFormId;

    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    axios
      .get(`${bankFormApi}${bankFormId}/`, { headers })
      .then((res) => {
        console.log("Bank form GET API response:", res.data);
        const data = res.data?.data || res.data;
        if (data) {
          const fields = [
            "account_number",
            "bank_branch",
            "ifsc_code",
            "bank_name",
            "account_holder_name",
            "bank_transaction_id",
            "account_exists",
            "bank_count",
            "phone_number",
            "remarks",
          ];

          fields.forEach((field) => {
            if (data[field] !== undefined && data[field] !== null) {
              dispatch(updateBankField({ name: field, value: data[field] }));
            }
          });

          if (data?.account_number) {
            setIsAccountNumDisabled(true);
          }
          if (data?.account_holder_name) {
            setIsHolderNameDisabled(true);
          }

          const bankUrl =
            (typeof data?.bank_id === "object" && data?.bank_id !== null ? data?.bank_id?.media_url || data?.bank_id?.url : "") ||
            (typeof data?.bank_id === "string" ? data?.bank_id : "") ||
            (typeof data?.bank === "object" && data?.bank !== null ? data?.bank?.media_url || data?.bank?.url : "") ||
            (typeof data?.bank === "string" ? data?.bank : "") ||
            data?.bank_image ||
            data?.passbook_image ||
            "";

          if (bankUrl) {
            dispatch(
              setBankImage({
                name: "document",
                image: {
                  name: "Bank Document",
                  url: bankUrl,
                  s3Url: bankUrl,
                },
              })
            );
            dispatch(updateBankField({ name: "bank_id", value: bankUrl }));
            dispatch(updateBankField({ name: "bank", value: bankUrl }));
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching Bank form GET API:", err);
      })
      .finally(() => {
        dispatch(getLoaderAction2(false));
      });
  }, [dispatch, selfonboardData, employeeDetails]);

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateBankField({ name, value }));
    clearError(name);
  };

  const updateNumberField = (name, value) => {
    updateField(name, value.replace(/\D/g, ""));
  };

  // Penny Drop Verification
  const handleVerifyBank = async () => {
    const accNo = String(bankDetails.account_number || "").trim();
    const ifsc = String(bankDetails.ifsc_code || "").trim().toUpperCase();
    const phone = String(
      bankDetails.phone_number ||
      basicInformation.phoneNum ||
      basicInformation.phone_number ||
      selfonboardData?.phone_number ||
      ""
    ).replace(/\D/g, "");
    const holderName = String(
      bankDetails.account_holder_name ||
      basicInformation.Name ||
      basicInformation.name ||
      basicInformation.first_name ||
      ""
    ).trim();

    if (!accNo || !ifsc) {
      responsiveToast("Please enter Account Number and IFSC code first", "error");
      return;
    }

    setIsVerifyingBank(true);
    dispatch(getLoaderAction2(true));

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    try {
      const payload = {
        account_number: accNo,
        ifsc_code: ifsc,
        account_holder_name: holderName,
        phone_number: phone,
      };

      console.log("Submitting Bank Penny Drop payload:", payload);
      const res = await axios.post(bankFormPennyDropApi, payload, { headers });
      console.log("Bank Penny Drop API response:", res.data);

      const outerData = res.data?.data || {};
      const innerData = outerData?.data || outerData;
      const innerCode = outerData?.code || res.data?.code;
      const accountExists = innerData?.account_exists !== undefined ? innerData.account_exists : (outerData?.account_exists !== undefined ? outerData.account_exists : true);
      const transactionId = outerData?.transaction_id || innerData?.transaction_id || innerData?.bank_transaction_id || res.data?.transaction_id || "";
      const rawNameAtBank = innerData?.name_at_bank || outerData?.name_at_bank || res.data?.name_at_bank || innerData?.account_holder_name || "";
      const nameAtBank = removeHonorifics(rawNameAtBank);

      if (innerCode === 200 || accountExists) {
        updateField("account_exists", true);
        if (transactionId) {
          updateField("bank_transaction_id", transactionId);
        }
        if (nameAtBank) {
          updateField("account_holder_name", String(nameAtBank).trim().toUpperCase());
          setIsHolderNameDisabled(true);
        }
        responsiveToast(res.data?.message || innerData?.message || outerData?.message || "Bank account verified successfully", "success");
      } else {
        updateField("account_exists", false);
        responsiveToast(innerData?.message || outerData?.message || res.data?.message || "Bank verification failed", "error");
      }
    } catch (err) {
      console.error("Bank Penny Drop verification error:", err);
      responsiveToast(
        err.response?.data?.message || err.response?.data?.detail || "Bank verification failed",
        "error"
      );
    } finally {
      dispatch(getLoaderAction2(false));
      setIsVerifyingBank(false);
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      responsiveToast("File size exceeds 10MB", "error");
      return;
    }

    setSelectedImageEvent(event);
    setCropperOpen(true);
  };

  const getCroppedImg = (imageRef, crop, fileName) => {
    const canvas = document.createElement("canvas");
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], fileName, {
          type: "image/jpeg",
        });

        setCropperOpen(false);
        handleImageChange(croppedFile);
        resolve(croppedFile);
      }, "image/jpeg");
    });
  };

  const handleImageChange = async (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (bankImages.document?.url) {
      URL.revokeObjectURL(bankImages.document.url);
    }

    const localUrl = URL.createObjectURL(file);

    dispatch(
      setBankImage({
        name: "document",
        image: {
          name: file.name,
          url: localUrl,
          s3Url: "",
          file: file,
        },
      })
    );

    updateField("bank", localUrl);
    clearError("document");

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    try {
      // 1. Presigned S3 Upload
      const folderName = "bank";
      const randomNumber = Math.floor(Math.random() * 10000000000);
      const fileExt = file.name.split(".").pop() || "jpg";
      const formattedFilename = `onboarding/${folderName}/image_cropper_${randomNumber}.${fileExt}`;

      const presignedReqData = {
        multiple_files: [
          {
            filename: formattedFilename,
            file_type: file.type || "image/jpeg",
          },
        ],
      };

      const presignedRes = await axios.post(PresentUrl, presignedReqData, { headers });
      const uploadedUrls = presignedRes.data?.data || presignedRes.data;

      let s3CleanUrl = "";
      if (Array.isArray(uploadedUrls) && uploadedUrls.length > 0) {
        const presignedPutUrl = uploadedUrls[0];
        s3CleanUrl = String(presignedPutUrl).split("?")[0];

        await fetch(presignedPutUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "image/jpeg",
            "x-amz-acl": "public-read",
          },
          body: file,
        });
      }

      const finalS3Url = s3CleanUrl || localUrl;

      dispatch(
        setBankImage({
          name: "document",
          image: {
            name: file.name,
            url: finalS3Url,
            s3Url: finalS3Url,
            file: file,
          },
        })
      );

      updateField("bank", finalS3Url);
      updateField("bank_id", finalS3Url);

      // 2. OCR Bank Fetch API Call
      try {
        const formData = new FormData();
        formData.append("bank_id", file);
        formData.append("file", file);

        const docType = bankDetails.documentType || "passbook";
        const ocrRes = await axios.post(
          `${bankFormFetchApi}?file_type=${docType}`,
          formData,
          { headers }
        );

        console.log("Bank OCR fetch response object:", ocrRes.data);

        let workingData = ocrRes?.data?.data || ocrRes?.data;
        let joinedString = "";

        if (Array.isArray(workingData) && workingData.length > 0) {
          joinedString = workingData.join("");
        } else if (typeof workingData === "string") {
          joinedString = workingData;
        }

        let level1 = null;
        if (joinedString) {
          try {
            level1 = JSON.parse(joinedString);
          } catch (e) {
            console.error("Error parsing joinedString:", e);
          }
        } else if (typeof workingData === "object" && workingData !== null) {
          level1 = workingData;
        }

        let innerParsedJson = null;
        if (level1?.data?.text) {
          if (typeof level1.data.text === "string") {
            try {
              innerParsedJson = JSON.parse(level1.data.text);
            } catch (e) {
              console.error("Error parsing level1.data.text:", e);
            }
          } else {
            innerParsedJson = level1.data.text;
          }
        }

        const extracted = innerParsedJson || level1?.data || level1;
        console.log("Extracted Bank Details final object:", extracted);

        if (extracted && typeof extracted === "object") {
          const holderName =
            extracted.name ||
            extracted.account_holder_name ||
            extracted.accountHolderName ||
            extracted.account_name ||
            "";

          const accNum =
            extracted.accountNumber ||
            extracted.account_number ||
            extracted.account_no ||
            extracted.accNo ||
            "";

          const ifscCode =
            extracted.ifsc ||
            extracted.ifsc_code ||
            extracted.ifscCode ||
            "";

          const bName =
            extracted.bankName ||
            extracted.bank_name ||
            extracted.bank ||
            "";

          const bBranch =
            extracted.branch ||
            extracted.bank_branch ||
            extracted.branch_name ||
            "";

          console.log("Updating fields with extracted values:", { holderName, accNum, ifscCode, bName, bBranch });

          if (accNum) {
            updateField("account_number", String(accNum).replace(/\D/g, ""));
            setIsAccountNumDisabled(true);
          }
          if (ifscCode) {
            updateField("ifsc_code", String(ifscCode).trim().toUpperCase());
          }
          if (bName) {
            updateField("bank_name", String(bName).trim().toUpperCase());
          }
          if (bBranch) {
            updateField("bank_branch", String(bBranch).trim().toUpperCase());
          }

          responsiveToast("Bank details extracted successfully", "success");
        }
      } catch (ocrErr) {
        console.error("Bank OCR detail fetch error:", ocrErr);
      }
    } catch (err) {
      console.error("Presigned URL upload error for Bank:", err);
    } finally {
      dispatch(getLoaderAction2(false));
    }
  };

  const removeImage = () => {
    if (bankImages.document?.url) {
      URL.revokeObjectURL(bankImages.document.url);
    }

    dispatch(removeBankImage("document"));
    updateField("bank", "");
    updateField("bank_id", "");
    updateField("account_holder_name", "");
    updateField("bank_name", "");
    updateField("bank_branch", "");
    updateField("ifsc_code", "");
    updateField("account_number", "");
    updateField("account_exists", false);
    updateField("bank_transaction_id", "");
    setIsAccountNumDisabled(false);
    setIsHolderNameDisabled(false);
    clearError("document");
  };

  const details = selfonboardData || employeeDetails;
  const bankFormId = extractBankFormId(details);

  const isBankVerified = Boolean(
    bankDetails.account_exists === true ||
    bankDetails.account_exists === "true" ||
    bankDetails.bank_transaction_id
  );

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();

    if (!bankImages.document && !bankDetails.bank) {
      missing.push("Bank Document");
      missingNames.add("document");
    }

    if (!String(bankDetails.account_holder_name || "").trim()) {
      missing.push("Account Holder Name");
      missingNames.add("account_holder_name");
    }

    if (!String(bankDetails.bank_name || "").trim()) {
      missing.push("Bank Name");
      missingNames.add("bank_name");
    }

    if (!String(bankDetails.bank_branch || "").trim()) {
      missing.push("Bank Branch");
      missingNames.add("bank_branch");
    }

    if (!String(bankDetails.ifsc_code || "").trim()) {
      missing.push("IFSC Code");
      missingNames.add("ifsc_code");
    }

    if (!String(bankDetails.account_number || "").trim()) {
      missing.push("Account Number");
      missingNames.add("account_number");
    }

    if (!isBankVerified) {
      missing.push("Bank Verification (Click Verify button)");
      missingNames.add("account_number");
    }

    setErrorFields(missingNames);

    return missing;
  }, [bankDetails, bankImages, isBankVerified]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields]
  );

  return (
    <div className={styles.bankDetails}>
      <header className={styles.sectionHeader}>Bank Details</header>

      <div className={styles.content}>
        <p className={styles.note}>
          Note: Upload the cheque leaf or passbook image. Supported formats:
          JPG, JPEG, PNG. Maximum size: 10 MB.
        </p>

        {/* Document Type */}
        <div className={styles.docType}>
          <label>
            <input
              type="checkbox"
              checked={bankDetails.documentType === "cheque"}
              onChange={() => updateField("documentType", "cheque")}
            />
            Cheque Leaf
          </label>

          <label>
            <input
              type="checkbox"
              checked={bankDetails.documentType === "passbook"}
              onChange={() => updateField("documentType", "passbook")}
            />
            Passbook
          </label>
        </div>

        {/* Upload */}
        <div className={styles.uploadBlock}>
          <div className={styles.previewRow}>
            {bankImages.document ? (
              <>
                <img
                  src={bankImages.document.url}
                  className={styles.imagePreview}
                  alt="Bank document preview"
                />
                <button
                  className={styles.removeButton}
                  onClick={removeImage}
                  type="button"
                >
                  Remove Image
                </button>
              </>
            ) : bankDetails.bank || bankDetails.bank_id ? (
              <>
                <img
                  src={
                    typeof bankDetails.bank_id === "object" && bankDetails.bank_id !== null
                      ? bankDetails.bank_id.media_url || bankDetails.bank_id.url
                      : bankDetails.bank_id || bankDetails.bank
                  }
                  className={styles.imagePreview}
                  alt="Bank document preview"
                />
                <button
                  className={styles.removeButton}
                  onClick={removeImage}
                  type="button"
                >
                  Remove Image
                </button>
              </>
            ) : (
              <label className={styles.uploadButton}>
                Upload Image
                <input
                  className={styles.hiddenInput}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileInputChange}
                />
              </label>
            )}
          </div>

          {errorFields.has("document") && (
            <p className={styles.fieldError}>
              Please upload a cheque leaf or passbook image
            </p>
          )}
        </div>

        {/* Form */}
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>
              Account Holder Name<b>*</b>
            </span>
            <input
              value={bankDetails.account_holder_name || ""}
              disabled={Boolean((isBankVerified || isHolderNameDisabled) && bankDetails.account_holder_name)}
              className={
                errorFields.has("account_holder_name") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField("account_holder_name", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("account_holder_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Bank Name<b>*</b>
            </span>
            <input
              value={bankDetails.bank_name || ""}
              className={errorFields.has("bank_name") ? styles.inputError : ""}
              onChange={(e) =>
                updateField("bank_name", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("bank_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Bank Branch<b>*</b>
            </span>
            <input
              value={bankDetails.bank_branch || ""}
              className={
                errorFields.has("bank_branch") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField("bank_branch", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("bank_branch") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              IFSC Code<b>*</b>
            </span>
            <input
              value={bankDetails.ifsc_code || ""}
              className={errorFields.has("ifsc_code") ? styles.inputError : ""}
              onChange={(e) =>
                updateField("ifsc_code", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("ifsc_code") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Account Number<b>*</b>
            </span>
            <div className={styles.inputWithAction}>
              <input
                value={bankDetails.account_number || ""}
                inputMode="numeric"
                disabled={Boolean((isBankVerified || isAccountNumDisabled) && bankDetails.account_number)}
                className={
                  errorFields.has("account_number") ? styles.inputError : ""
                }
                onChange={(e) =>
                  updateNumberField("account_number", e.target.value)
                }
              />
              {isBankVerified ? (
                <span className={styles.verifiedBadge}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#047857"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Verified
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.verifyBtn}
                  disabled={
                    !bankDetails.account_number || !bankDetails.ifsc_code || isVerifyingBank
                  }
                  onClick={handleVerifyBank}
                >
                  {isVerifyingBank ? "Verifying..." : "Verify"}
                </button>
              )}

            </div>
            {errorFields.has("account_number") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          {/* <label className={styles.field}>
            <span>Phone Number</span>
            <input
              value={bankDetails.phone_number || ""}
              inputMode="numeric"
              onChange={(e) =>
                updateNumberField("phone_number", e.target.value)
              }
            />
          </label> */}

          <label className={styles.field}>
            <span>Remarks</span>
            <input
              value={bankDetails.remarks || ""}
              onChange={(e) => updateField("remarks", e.target.value)}
            />
          </label>
        </div>
      </div>

      {cropperOpen && selectedImageEvent && (
        <ImageCropper
          updateAvatar={() => { }}
          selectedImage={selectedImageEvent}
          getCroppedImg={getCroppedImg}
          closeModal={() => setCropperOpen(false)}
        />
      )}
    </div>
  );
});

export default BankDetails;
